<template>
  <AePageLayout v-model:sidebar-collapsed="sidebarCollapsed" v-model:mobile-open="sidebarMobileOpen" :floating="floating">
    <!-- Sidebar -->
    <template #sidebar>
      <AeSidebar
        v-model:collapsed="sidebarCollapsed"
        :items="effectiveSidebarItems"
        :active-item="activeItem"
        :title="sidebarTitle || skinStore.sidebarTitle || undefined"
        :floating="floating"
        @navigate="handleNavigate"
        @close="sidebarMobileOpen = false"
      >
        <template v-if="!skinStore.sidebarTitle && (skinStore.logo || skinStore.icon)" #header>
          <div class="sidebar-brand">
            <img v-if="skinStore.logo" :src="skinStore.logo" class="sidebar-brand__logo" alt="logo" />
            <AeIcon v-else-if="skinStore.icon" :name="skinStore.icon" :size="28" />
          </div>
        </template>
        <template #footer>
          <div class="sidebar-footer">
            <span class="version-info" @dblclick="showDebug = true" title="Doppio click per debug">v{{ version }}</span>
          </div>
        </template>
      </AeSidebar>
    </template>

    <!-- Navbar -->
    <template #navbar>
      <AeNavbar
        :title="hideNavbarTitle ? undefined : (isMobileApp && effectiveTitle ? effectiveTitle : (navbarTitle || skinStore.appName || undefined))"
        :floating="floating"
        @toggle-sidebar="handleSidebarToggle"
      >      
        <template v-if="(hideNavbarTitle || !skinStore.appName) && (skinStore.logo || skinStore.icon) && !(isMobileApp && effectiveTitle && !hideNavbarTitle)" #left>
          <img v-if="skinStore.logo" :src="skinStore.logo" class="navbar-brand__logo" alt="logo" />
          <AeIcon v-else-if="skinStore.icon" :name="skinStore.icon" :size="22" />
        </template>
        <template #right>
          <SyncMonitor />
          <AuthComponent />
        </template>
      </AeNavbar>
    </template>

    <!-- Body -->
    <div class="layout-body" :class="{ 'layout-body--mobile': isMobileApp }">
      <!-- DB Error State -->
      <template v-if="hasDbError">
        <AePageHeader
          :title="t('poderiLayout.dbErrorTitle')"
          :subtitle="t('poderiLayout.dbErrorSubtitle')"
          :floating="floating"
        />
        <AePageContent>
          <div class="row g-3">
            <div class="col-12">
              <AeCard>
                <div class="error-content">
                  <h3>{{ t('poderiLayout.dbErrorHeading') }}</h3>
                  <div class="error-details">
                    <p v-for="(error, index) in dbErrors" :key="index">
                      {{ error }}
                    </p>
                  </div>
                  <AeButton variant="primary" @click="retryInit">
                    {{ t('poderiLayout.dbErrorRetry') }}
                  </AeButton>
                </div>
              </AeCard>
            </div>
          </div>
        </AePageContent>
      </template>

      <!-- Main Content -->
      <template v-else>

        <!-- Invitation done message -->
        <div v-if="invitationDoneMessage" class="inv-banner inv-banner--success">
          <span>{{ invitationDoneMessage }}</span>
          <button class="inv-banner__close" @click="invitationDoneMessage = null">✕</button>
        </div>

        <!-- Pending invitations banner -->
        <div v-if="orgStore.pendingInvitations.length > 0" class="inv-banner">
          <div class="inv-banner__content">
            <strong>Inviti in attesa:</strong>
            <span
              v-for="inv in orgStore.pendingInvitations"
              :key="inv.id_invitation"
              class="inv-banner__item"
              @click="openInvitation(inv)"
            >
              {{ inv.target_name }}
            </span>
          </div>
        </div>

        <!-- Desktop header: non montato su mobile, v-show per mantenere il target Teleport -->
        <div v-if="!isMobileApp" v-show="showHeader && effectiveTitle">
          <AePageHeader
            :title="effectiveTitle"
            :subtitle="effectiveSubtitle"
            :breadcrumbs="effectiveBreadcrumbs"
            :back-button="backButton"
            :floating="floating"
            @back="handleBack"
          >
            <template #actions>
              <slot name="actions"></slot>
              <div :id="actionsTargetId" class="actions-target"></div>
            </template>
          </AePageHeader>
        </div>

        <!-- Mobile title bar non serve il titolom lo abbiamo messo sopra -->
        <!-- <div v-if="isMobileApp && showHeader && effectiveTitle" class="mobile-title-bar">
          <span class="mobile-title">{{ effectiveTitle }}</span>
        </div> -->

        <AeToolbar
          v-if="showToolbar"
          :searchable="searchable"
          v-model:search-value="searchValue"
          :search-placeholder="searchPlaceholder"
          @search="handleSearch"
        >
          <template #right>
            <slot name="toolbar"></slot>
          </template>
        </AeToolbar>

        <AePageContent :loading="loading" :padded="false">
          <slot></slot>
        </AePageContent>
      </template>

      <!-- Debug Panel -->
      <DebugPanel v-model="showDebug" />


<!-- Mobile Action Bar (sticky footer, solo in modalità app) -->
      <div
        v-if="isMobileApp"
        class="mobile-action-bar"
        :style="{ bottom: footerBottom + 'px' }"
      >
        <button
          v-if="backTarget"
          type="button"
          class="mobile-back-btn"
          @click="handleMobileBack"
        >
          <AeIcon name="ChevronLeft" :size="22" />
        </button>
        <div class="mobile-actions">
          <slot name="actions"></slot>
          <div :id="actionsTargetId" class="actions-target"></div>
        </div>
      </div>
    </div>

    <!-- Web footer (solo su desktop) -->
    <template v-if="showFooter && !isMobileApp">
      <footer class="poderi-footer">
        <slot name="footer">
          <small v-html="skinStore.footer"></small>
        </slot>
      </footer>
    </template>
  <InvitationModal
    :invitation="selectedInvitation"
    @close="selectedInvitation = null"
    @done="handleInvitationDone"
  />
  </AePageLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useDbStore } from '../stores/dbStore'
import { useAppStore } from '../stores/app'
import { useSkinStore } from '../stores/skinStore'
import { useSidebar } from '../composables/useSidebar'
import { isNative } from '../services/platform.js'
import {
  AePageLayout,
  AeSidebar,
  AeNavbar,
  AePageHeader,
  AePageContent,
  AeToolbar,
  AeButton,
  AeCard,
  AeIcon
} from '@aevue/components'
import AuthComponent from '../components/AuthComponent.vue'
import SyncMonitor from '../components/SyncMonitor.vue'
import DebugPanel from '../components/DebugPanel.vue'
import InvitationModal from '../components/InvitationModal.vue'
import { useOrgStore } from '../stores/orgStore'
import { getMobileVersion } from '../services/platform.js'

const version = computed(() => {
  // Su mobile usa la versione da project.json, su web usa VERSION root
  const mobileVer = getMobileVersion()
  return mobileVer || import.meta.env.VITE_APP_VERSION || 'dev'
})

const { getSidebarRoot } = useSidebar()

const props = defineProps({
  // Page Header
  showHeader: {
    type: Boolean,
    default: true
  },
  title: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  },
  breadcrumbs: {
    type: Array,
    default: () => []
  },
  backButton: {
    type: Boolean,
    default: false
  },

  // Sidebar
  sidebarItems: {
    type: Array,
    default: () => []
  },
  sidebarTitle: {
    type: String,
    default: ''
  },
  activeItem: {
    type: String,
    default: ''
  },

  // Navbar
  navbarTitle: {
    type: String,
    default: ''
  },
  hideNavbarTitle: {
    type: Boolean,
    default: false
  },

  // Toolbar
  showToolbar: {
    type: Boolean,
    default: false
  },
  searchable: {
    type: Boolean,
    default: false
  },
  searchPlaceholder: {
    type: String,
    default: 'Cerca...'
  },

  // Layout options
  floating: {
    type: Boolean,
    default: true
  },
  showFloatingToggle: {
    type: Boolean,
    default: true
  },
  showFooter: {
    type: Boolean,
    default: true
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['back', 'navigate', 'search', 'update:searchValue'])

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const dbStore = useDbStore()
const appStore = useAppStore()
const skinStore = useSkinStore()
const orgStore = useOrgStore()

const sidebarCollapsed = ref(false)
const sidebarMobileOpen = ref(false)
const floating = ref(props.floating)
const searchValue = ref('')
const showDebug = ref(false)

const selectedInvitation = ref(null)
const invitationDoneMessage = ref(null)

function openInvitation(inv) {
  selectedInvitation.value = inv
}

function handleInvitationDone({ newProfile }) {
  selectedInvitation.value = null
  if (newProfile) {
    invitationDoneMessage.value = 'Nuovo profilo creato. Potrai selezionarlo al prossimo login.'
  } else {
    window.location.reload()
  }
}

// Modalità app: Capacitor nativo OPPURE schermo stretto (mobile web)
const isMobileApp = ref(isNative() || window.innerWidth < 768)

// Posizione footer sopra la tastiera virtuale (via visualViewport)
const footerBottom = ref(0)

function updateFooterPosition() {
  const vv = window.visualViewport
  if (!vv) return
  const keyboardHeight = window.innerHeight - (vv.offsetTop + vv.height)
  footerBottom.value = Math.max(0, keyboardHeight)
}

// Injected header config (per componenti embedded che chiamano setHeader)
const injectedHeader = ref(null)
const actionsTargetId = `poderi-actions-${Math.random().toString(36).slice(2, 8)}`
provide('poderiLayoutSetHeader', (config) => { injectedHeader.value = config })
provide('poderiLayoutClearHeader', () => { injectedHeader.value = null })
provide('poderiLayoutActionsId', actionsTargetId)
const effectiveTitle = computed(() => injectedHeader.value?.title ?? props.title)
const effectiveSubtitle = computed(() => injectedHeader.value?.subtitle ?? props.subtitle)
const effectiveBreadcrumbs = computed(() => injectedHeader.value?.breadcrumbs ?? props.breadcrumbs)

// Back target per il pulsante mobile (penultimo breadcrumb con `to`)
const backTarget = computed(() => {
  const crumbs = effectiveBreadcrumbs.value
  if (!crumbs || crumbs.length < 2) return null
  for (let i = crumbs.length - 2; i >= 0; i--) {
    if (crumbs[i].to) return crumbs[i]
  }
  return null
})

// DB state
const hasDbError = computed(() => dbStore.errors.length > 0)
const dbErrors = computed(() => dbStore.errors)

const effectiveSidebarItems = computed(() => {
  return props.sidebarItems.length > 0 ? props.sidebarItems : getSidebarRoot()
})

function handleSidebarToggle() {
  if (window.innerWidth < 640) {
    sidebarMobileOpen.value = !sidebarMobileOpen.value
  } else {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }
}

function handleNavigate(item) {
  sidebarMobileOpen.value = false
  emit('navigate', item)
  if (item.id === 'contact') {
    window.location.href = 'mailto:assistenza@aedit.it'
  } else if (item.to) {
    router.push(item.to)
  } else if (item.id === 'home') {
    router.push('/')
  } else if (item.id === 'poderi') {
    router.push('/poderi')
  } else if (item.id === 'dashboard') {
    router.push('/poderi/dashboard')
  } else if (item.id === 'settings') {
    router.push('/settings')
  }
}

function handleBack() {
  emit('back')
  router.back()
}

function handleMobileBack() {
  if (backTarget.value?.to) {
    router.push(backTarget.value.to)
  } else {
    router.back()
  }
}

function handleSearch(value) {
  emit('search', value)
}

async function retryInit() {
  try {
    await dbStore.init(appStore.dexieConfig)
  } catch (error) {
    console.error('Failed to reinitialize DB:', error)
  }
}

watch(searchValue, (newValue) => {
  emit('update:searchValue', newValue)
})

const handleSwipeOpenMenu = () => {
  sidebarMobileOpen.value = true
}

onMounted(() => {
  window.addEventListener('swipe-open-menu', handleSwipeOpenMenu)
  if (isMobileApp.value) {
    window.visualViewport?.addEventListener('resize', updateFooterPosition)
    window.visualViewport?.addEventListener('scroll', updateFooterPosition)
  }
  if (appStore.user?.uid) {
    orgStore.fetchPendingInvitationsOnce()
  }
})

onUnmounted(() => {
  window.removeEventListener('swipe-open-menu', handleSwipeOpenMenu)
  if (isMobileApp.value) {
    window.visualViewport?.removeEventListener('resize', updateFooterPosition)
    window.visualViewport?.removeEventListener('scroll', updateFooterPosition)
  }
})
</script>

<style scoped>

body {
  margin: 0px !important;
}

.navbar-brand__logo {
  height: 26px;
  object-fit: contain;
}

.sidebar-brand {
  padding: 12px 16px;
  display: flex;
  align-items: center;
}

.sidebar-brand__logo {
  height: 32px;
  object-fit: contain;
  max-width: 100%;
}

.sidebar-footer {
  padding: var(--spacing-2, 8px);
  text-align: center;
}

.version-info {
  font-size: 11px;
  opacity: 0.5;
  color: #000000;
  cursor: default;
  user-select: none;
}

.sync-status {
  font-size: 12px;
  opacity: 0.6;
  transition: opacity 0.3s;
}

.sync-status.synced {
  opacity: 0.8;
  color: var(--color-success, #10b981);
}

.error-content {
  text-align: center;
  padding: var(--spacing-6, 24px);
}

.error-content h3 {
  margin: 0 0 var(--spacing-4, 16px);
}

.error-details {
  background: var(--card-bg, rgba(255, 255, 255, 0.5));
  border: 1px solid var(--border-color, rgba(45, 55, 72, 0.1));
  border-radius: var(--border-radius, 8px);
  padding: var(--spacing-4, 16px);
  margin: var(--spacing-4, 16px) 0;
  text-align: left;
}

.error-details p {
  margin: var(--spacing-2, 8px) 0;
  color: var(--text-color, #2d3748);
  font-size: 14px;
}

.layout-body {
  flex: 1;
}

.inv-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 1rem;
  background: var(--ae-primary, #2563eb);
  color: #fff;
  font-size: 0.9rem;
}

.inv-banner--success {
  background: var(--ae-success, #059669);
}

.inv-banner__content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.inv-banner__item {
  background: rgba(255,255,255,0.2);
  border-radius: 4px;
  padding: 0.15rem 0.5rem;
  cursor: pointer;
  text-decoration: underline;
}

.inv-banner__item:hover {
  background: rgba(255,255,255,0.35);
}

.inv-banner__close {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 0.25rem;
  opacity: 0.8;
}

.inv-banner__close:hover {
  opacity: 1;
}

/* Padding inferiore per non coprire il contenuto con il footer mobile */
.layout-body--mobile {
  padding-bottom: calc(60px + env(safe-area-inset-bottom, 0px));
}

/* Titolo pagina su mobile (semplificato, senza breadcrumbs) */
.mobile-title-bar {
  padding: 6px 16px 4px;
}

.mobile-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-color, #e2e8f0);
  line-height: 1.3;
}

/* ── Mobile Action Bar ── */
.mobile-action-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;           /* sovrascritto dall'inline style per la tastiera */
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  /* safe area per iPhone X+ (home indicator) */
  padding-bottom: max(8px, env(safe-area-inset-bottom, 8px));
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.mobile-back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: #f1f5f9;
  color: var(--ae-primary, #2563eb);
  cursor: pointer;
}

.mobile-back-btn:active {
  opacity: 0.6;
}

.mobile-actions {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
}

/* Il div target del Teleport non deve occupare spazio nel flex:
   display:contents lo rende trasparente al layout, promuovendo i figli
   a flex item diretti del contenitore (sia header desktop che footer mobile) */
.actions-target {
  display: contents;
}

/* ── Web footer (solo desktop) ── */
.poderi-footer {
  padding: var(--spacing-4, 16px);
  text-align: center;
  border-top: 1px solid var(--border-color, rgba(45, 55, 72, 0.1));
  background: var(--card-bg, rgba(255, 255, 255, 0.5));
}

.poderi-footer small {
  color: var(--text-muted, #718096);
  font-size: 12px;
}

</style>
